import { FC, useEffect, useState } from 'react';
import { capitalize, makeStyles } from '@material-ui/core';
import CButton from 'src/component/Button';
import { isValidUrl } from 'src/util/inputValidation';
import { UrlPrefixType } from 'src/page/DynamicLinksConfig/interfaces/urlPrefixType.interface';
import { URL_PREFIX_TYPES_TO_DESCRIPTIONS } from 'src/page/DynamicLinksConfig/constants/urlPrefixTypesToDescriptions';
import AddOrEditUrlPrefixModal from 'src/page/DynamicLinksConfig/components/AddOrEditUrlPrefixModal';

const useStyle = makeStyles({
  header: {
    '&:not(:nth-child(1))': {
      borderTop: '1px solid #CAD8FF'
    },
    alignItems: 'center',
    background: '#F0F4FF88',
    display: 'flex',
    height: 'min-content',
    justifyContent: 'space-between',
    padding: '8px 8px 8px 16px'
  },
  headerTitle: {
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: '22px'
  },
  urlPrefix: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px'
  },
  urlPrefixRow: {
    alignItems: 'center',
    background: 'white',
    borderTop: '1px solid #CAD8FF',
    display: 'flex',
    height: 'min-content',
    justifyContent: 'space-between',
    padding: '8px 8px 8px 16px'
  }
});

interface UrlPrefixesSectionItemProps {
  handleUpdatePrefixes: (prefixes: string[]) => void;
  prefixes: string[];
  urlType: UrlPrefixType;
}

const UrlPrefixesSectionItem: FC<UrlPrefixesSectionItemProps> = ({
  handleUpdatePrefixes,
  prefixes,
  urlType
}) => {
  const classes = useStyle();

  const [modalState, setModalState] = useState<{ initialPrefix?: string; isOpen: boolean }>({
    initialPrefix: undefined,
    isOpen: false
  });

  const [prefix, setPrefix] = useState<string>('');

  const [error, setError] = useState<string | undefined>(undefined);

  const handleClose = () => {
    setModalState({ isOpen: false });
  };

  const handleCreate = (e: Event) => {
    e.preventDefault();

    if (!prefix?.length) {
      setError(`${capitalize(urlType)} URL prefix is empty`);
      return;
    }

    if (!isValidUrl(prefix)) {
      setError(`${capitalize(urlType)} URL prefix is invalid URL`);
      return;
    }

    if (prefixes.includes(prefix)) {
      setError(`${capitalize(urlType)} URL prefix already exists`);
      return;
    }

    handleUpdatePrefixes([...prefixes, prefix]);
    setModalState({ isOpen: false });
  };

  const handleDelete = (e: Event) => {
    e.preventDefault();

    handleUpdatePrefixes(prefixes.filter((p) => p !== modalState.initialPrefix));
    setModalState({ isOpen: false });
  };

  const handleEdit = (e: Event) => {
    e.preventDefault();

    const index = prefixes.findIndex((p) => p === modalState.initialPrefix);
    const updatedPrefixes = [...prefixes];
    updatedPrefixes.splice(index, 1, prefix);

    handleUpdatePrefixes(updatedPrefixes);
    setModalState({ isOpen: false });
  };

  const handlePrefixChange = (value: string) => {
    // eslint-disable-next-line no-useless-escape
    if (!/[^-a-zA-Z0-9()@:%_\+.~#?&//=]/.test(value)) {
      setPrefix(value);
    }
  };

  useEffect(() => {
    if (!modalState.isOpen) {
      return;
    }

    setError(undefined);
    setPrefix(modalState.initialPrefix ?? '');
  }, [modalState.isOpen]);

  return (
    <>
      <div className={classes.header}>
        <div className={classes.headerTitle}>{capitalize(urlType)} URL prefixes</div>

        <CButton onClick={() => setModalState({ isOpen: true })} type="primary">
          Add
        </CButton>
      </div>

      {prefixes.map((pref) => (
        <div key={pref} className={classes.urlPrefixRow}>
          <div className={classes.urlPrefix}>{pref}</div>

          <CButton
            onClick={() => setModalState({ initialPrefix: pref, isOpen: true })}
            type="secondary"
          >
            Edit
          </CButton>
        </div>
      ))}

      <AddOrEditUrlPrefixModal
        handleClose={handleClose}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        initialInputGroupValue={modalState.initialPrefix}
        inputGroupDescription={URL_PREFIX_TYPES_TO_DESCRIPTIONS[urlType]}
        inputGroupError={error}
        inputGroupHtmlFor={`standard-input-${urlType.replaceAll(' ', '-')}-url-prefix`}
        inputGroupLabel={`${capitalize(urlType)} URL prefix`}
        inputGroupOnChange={handlePrefixChange}
        inputGroupValue={prefix}
        isOpen={modalState.isOpen}
        urlType={urlType}
      />
    </>
  );
};

export default UrlPrefixesSectionItem;
